"use client";
import React from 'react';
import { motion } from 'framer-motion';

const stats = [
    {
        value: '919',
        label: 'conversations generated in 30 days for a luxury resort',
        icon: (
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8 10C8 7.79 9.79 6 12 6H36C38.21 6 40 7.79 40 10V28C40 30.21 38.21 32 36 32H28L20 42V32H12C9.79 32 8 30.21 8 28V10Z" stroke="#3730a3" strokeWidth="2.2" strokeLinejoin="round"/>
                <circle cx="17" cy="19" r="2.5" fill="#3730a3"/>
                <circle cx="24" cy="19" r="2.5" fill="#3730a3"/>
                <circle cx="31" cy="19" r="2.5" fill="#3730a3"/>
            </svg>
        ),
        accent: 'rgba(55,48,163,0.10)',
        ring: 'rgba(55,48,163,0.18)',
    },
    {
        value: '70%+',
        label: 'lead-to-client conversion rates across tracked campaigns',
        icon: (
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 34L20 22L27 29L38 14" stroke="#3730a3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M33 14H38V19" stroke="#3730a3" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="34" cy="36" r="5" stroke="#3730a3" strokeWidth="2"/>
                <path d="M31 36L33 38L37 34" stroke="#3730a3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        accent: 'rgba(55,48,163,0.08)',
        ring: 'rgba(55,48,163,0.15)',
    },
    {
        value: 'Proven',
        label: 'growth across healthcare, real estate, interior design, education, SaaS, and ecommerce',
        icon: (
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="7" y="28" width="7" height="14" rx="2" fill="#3730a3" fillOpacity="0.25" stroke="#3730a3" strokeWidth="2"/>
                <rect x="20" y="20" width="7" height="22" rx="2" fill="#3730a3" fillOpacity="0.35" stroke="#3730a3" strokeWidth="2"/>
                <rect x="33" y="10" width="7" height="32" rx="2" fill="#3730a3" fillOpacity="0.5" stroke="#3730a3" strokeWidth="2"/>
                <path d="M10 18L20 12L28 16L38 6" stroke="#3730a3" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M34 6H38V10" stroke="#3730a3" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
        ),
        accent: 'rgba(55,48,163,0.06)',
        ring: 'rgba(55,48,163,0.13)',
    },
];

const IndustryPill = ({ label }: { label: string }) => (
    <span
        className="inline-block px-3 py-1 rounded-full text-xs font-semibold mr-2 mb-2"
        style={{
            background: 'rgba(55,48,163,0.08)',
            color: '#3730a3',
            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
            border: '1px solid rgba(55,48,163,0.18)',
            letterSpacing: '0.04em',
        }}
    >
        {label}
    </span>
);

export default function PerformanceGoals() {
    return (
        <section className="relative bg-white overflow-hidden py-24 px-6 md:px-16 lg:px-24">

            {/* Decorative background dot grid */}
            <div
                className="absolute inset-0 pointer-events-none"
                style={{
                    backgroundImage: 'radial-gradient(circle, rgba(55,48,163,0.07) 1.5px, transparent 1.5px)',
                    backgroundSize: '32px 32px',
                    maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                    WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                }}
            />

            {/* Decorative blobs */}
            <div className="absolute top-0 right-0 pointer-events-none" style={{ width: 420, height: 420, background: 'radial-gradient(circle, rgba(55,48,163,0.06) 0%, transparent 70%)', transform: 'translate(30%, -30%)' }} />
            <div className="absolute bottom-0 left-0 pointer-events-none" style={{ width: 320, height: 320, background: 'radial-gradient(circle, rgba(55,48,163,0.05) 0%, transparent 70%)', transform: 'translate(-20%, 20%)' }} />

            <div className="max-w-[1400px] mx-auto flex flex-col gap-20 relative z-10">

                {/* Heading */}
                <div className="flex flex-col gap-5">
                    <motion.span
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.2em] uppercase"
                        style={{ color: '#3730a3', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                    >
                        Real Results
                    </motion.span>
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="text-5xl md:text-7xl font-bold tracking-tight leading-none"
                        style={{
                            fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                            color: '#3730a3',
                        }}
                    >
                        Real Results. Real Businesses.
                    </motion.h2>
                </div>

                {/* Stats grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {stats.map((s, i) => (
                        <motion.div
                            key={i}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: i * 0.15 }}
                            className="relative flex flex-col gap-6 p-8 rounded-3xl group overflow-hidden"
                            style={{
                                background: 'rgba(255,255,255,0.9)',
                                border: '1.5px solid rgba(55,48,163,0.12)',
                                boxShadow: '0 4px 32px rgba(55,48,163,0.06)',
                                backdropFilter: 'blur(8px)',
                                transition: 'box-shadow 0.3s, border-color 0.3s',
                            }}
                            whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                        >
                            {/* Card glow on hover */}
                            <div
                                className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 pointer-events-none"
                                style={{
                                    background: 'radial-gradient(ellipse at top left, rgba(55,48,163,0.07) 0%, transparent 70%)',
                                    transition: 'opacity 0.4s',
                                }}
                            />

                            {/* Icon */}
                            <div className="relative flex items-center gap-5">
                                <div
                                    className="shrink-0 rounded-2xl flex items-center justify-center"
                                    style={{
                                        width: 80,
                                        height: 80,
                                        background: `linear-gradient(135deg, ${s.ring} 0%, ${s.accent} 100%)`,
                                        border: '1.5px solid rgba(55,48,163,0.15)',
                                    }}
                                >
                                    {s.icon}
                                </div>
                                <div
                                    className="absolute -top-4 -right-4 rounded-full opacity-30 pointer-events-none"
                                    style={{
                                        width: 100,
                                        height: 100,
                                        background: `radial-gradient(circle, ${s.ring} 0%, transparent 70%)`,
                                    }}
                                />
                            </div>

                            {/* Number */}
                            <div className="flex flex-col gap-1">
                                <span
                                    className="font-bold leading-none"
                                    style={{
                                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                                        fontSize: 'clamp(52px, 5vw, 72px)',
                                        color: '#3730a3',
                                        letterSpacing: '-0.01em',
                                    }}
                                >
                                    {s.value}
                                </span>
                                <div style={{ width: 48, height: 3, background: 'linear-gradient(90deg, #3730a3, transparent)', borderRadius: 2 }} />
                            </div>

                            {/* Label */}
                            <p
                                className="text-base leading-relaxed"
                                style={{
                                    fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                    color: 'rgba(8,4,0,0.65)',
                                    letterSpacing: '0.02em',
                                    fontWeight: 400,
                                }}
                            >
                                {s.label}
                            </p>
                        </motion.div>
                    ))}
                </div>

                {/* Industry pills */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                    className="flex flex-col gap-4"
                >
                    <p className="text-xs font-bold tracking-[0.18em] uppercase" style={{ color: 'rgba(55,48,163,0.5)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                        Industries we serve
                    </p>
                    <div className="flex flex-wrap">
                        {['Healthcare', 'Real Estate', 'Interior Design', 'Education', 'SaaS', 'Ecommerce', 'Aesthetic Clinics', 'Finance', 'Hospitality'].map(ind => (
                            <IndustryPill key={ind} label={ind} />
                        ))}
                    </div>
                </motion.div>

                {/* Tagline */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    className="flex flex-col gap-4"
                >
                    <div style={{ height: 1, background: 'linear-gradient(90deg, #3730a3 0%, transparent 100%)', opacity: 0.2 }} />
                    <p
                        className="text-lg md:text-xl"
                        style={{
                            fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                            color: '#3730a3',
                            letterSpacing: '0.04em',
                            fontWeight: 400,
                        }}
                    >
                        No vanity metrics. No guesswork. Just marketing rooted in understanding and built for ROI.
                    </p>
                </motion.div>

            </div>
        </section>
    );
}
